// =============================================================================
// useLogCollectorClient Hook
// =============================================================================

import { useMemo } from 'react';
import { ApiClient, type ApiClientConfig } from '../lib/api.js';
import { useLogCollectorContext } from './context.js';

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = 'log-collector-token';

// =============================================================================
// Helpers
// =============================================================================

/**
 * Get token from localStorage (browser only).
 */
function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Store token in localStorage (browser only).
 */
export function setStoredToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      localStorage.setItem(STORAGE_KEY, token);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

// =============================================================================
// Hook
// =============================================================================

export interface UseLogCollectorClientOptions {
  /** Override the base URL from context */
  baseUrl?: string;
  /** Session token override */
  sessionToken?: string;
  /** API key override */
  apiKey?: string;
  /** Whether to load token from localStorage on init (default: true) */
  useStoredToken?: boolean;
}

export interface UseLogCollectorClientResult {
  /** The API client instance */
  client: ApiClient;
  /** Base URL being used */
  baseUrl: string;
}

/**
 * Hook to get or create a log-collector API client.
 *
 * If used within a LogCollectorProvider, returns the client from context.
 * Otherwise, creates a new client with the provided options.
 *
 * @example
 * ```tsx
 * // Within provider - uses context client
 * function Dashboard() {
 *   const { client } = useLogCollectorClient();
 *   // ...
 * }
 *
 * // Without provider - creates new client
 * function StandaloneWidget() {
 *   const { client } = useLogCollectorClient({
 *     baseUrl: 'https://logs.example.com/api',
 *     apiKey: 'lc_xxx',
 *   });
 *   // ...
 * }
 * ```
 */
export function useLogCollectorClient(
  options: UseLogCollectorClientOptions = {}
): UseLogCollectorClientResult {
  const {
    baseUrl: overrideBaseUrl,
    sessionToken,
    apiKey,
    useStoredToken = true,
  } = options;

  // Try to get context (may be null if not in provider)
  let contextValue: ReturnType<typeof useLogCollectorContext> | null = null;
  try {
    contextValue = useLogCollectorContext();
  } catch {
    // Not in provider, will create new client
  }

  // Determine base URL
  const baseUrl = overrideBaseUrl ?? contextValue?.baseUrl ?? '/api';

  // Create or reuse client
  const client = useMemo(() => {
    // If we have context and no overrides, use context client
    if (contextValue && !overrideBaseUrl && !sessionToken && !apiKey) {
      // Sync stored token to context client
      if (useStoredToken) {
        const storedToken = getStoredToken();
        if (storedToken && !contextValue.client.getSessionToken()) {
          contextValue.client.setSessionToken(storedToken);
        }
      }
      return contextValue.client;
    }

    // Create new client with options
    const config: ApiClientConfig = {
      baseUrl,
      sessionToken: sessionToken ?? (useStoredToken ? getStoredToken() ?? undefined : undefined),
      apiKey,
    };

    return new ApiClient(config);
  }, [contextValue, baseUrl, sessionToken, apiKey, useStoredToken]);

  return { client, baseUrl };
}
